import { useEffect, useId, useState } from "react";
import { Button } from "../../components/Button";
import { BackupPill, backupLiveLine } from "./BackupPill";
import { GUIDE_FEATURES } from "./features";
import { GuideIcon } from "./GuideIcon";
import { useBackupIncr, type BackupIncrStatus } from "./useBackupIncr";
import "./guide.css";

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 11v5M12 8h.01" />
    </svg>
  );
}

type DialogProps = {
  open: boolean;
  onClose: () => void;
  backup: BackupIncrStatus | null;
};

function FacturxGuideDialog({ open, onClose, backup }: DialogProps) {
  const titleId = useId();
  const live = backupLiveLine(backup);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fx-guide-overlay" role="presentation">
      <button type="button" className="fx-guide-overlay__bg" aria-label="Fermer" onClick={onClose} />
      <div className="fx-guide" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="fx-guide__head">
          <div>
            <p className="fx-guide__kicker">Aurélia Facture</p>
            <h2 id={titleId} className="fx-guide__title">
              Principes & fonctionnement
            </h2>
          </div>
          <button type="button" className="fx-iconbtn" aria-label="Fermer" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </header>
        <div className="fx-guide__body">
          <p className="fx-guide__lead">
            <strong>Aurélia Facture</strong> : vous préparez, vous envoyez, le PDF d’origine est
            verrouillé et une copie de secours part toute seule. Stripe, PayPal et GoCardless sont
            connectés. GoCardless prélève une facture envoyée ; « Payé » se pose tout seul.
          </p>
          <div className="fx-guide__grid">
            {GUIDE_FEATURES.map((f) => (
              <article key={f.key} className={`fx-guide-card${f.key === "backup" ? " is-backup" : ""}`}>
                <span className="fx-guide-card__ico">
                  <GuideIcon tint={f.tint} />
                </span>
                <div>
                  <h3>{f.title}</h3>
                  <p>{f.text}</p>
                  {f.key === "backup" ? (
                    <p className={`fx-guide-card__live${live.ok ? " is-ok" : " is-bad"}`}>{live.text}</p>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        </div>
        <footer className="fx-guide__foot">
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            Fermer
          </Button>
        </footer>
      </div>
    </div>
  );
}

export function FacturxGuideModal() {
  const [open, setOpen] = useState(false);
  const backup = useBackupIncr(true);

  return (
    <>
      <BackupPill state={backup} onClick={() => setOpen(true)} />
      <button
        type="button"
        className="fx-iconbtn"
        aria-label="Principes & fonctionnement"
        title="Principes & fonctionnement"
        onClick={() => setOpen(true)}
      >
        <InfoIcon />
      </button>
      <FacturxGuideDialog open={open} onClose={() => setOpen(false)} backup={backup} />
    </>
  );
}
